import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { api, fmtKLTime, fmtKLDate } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/* Staff own history: date, in, out, hours, review status.
   No photos are ever shown back to staff (spec §7). */
export default function HistoryPage() {
  const { t } = useLang();
  const { data, isLoading } = useQuery({
    queryKey: ["my-history"],
    queryFn: async () => (await api.get("/punches/me/history")).data,
  });

  return (
    <AppShell>
      <div className="mx-auto max-w-md space-y-4">
        <h1 className="text-xl font-semibold tracking-tight">{t("myHistory")}</h1>
        {isLoading && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
          </div>
        )}
        {!isLoading && (!data?.days || data.days.length === 0) && (
          <p data-testid="history-empty" className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            {t("noHistory")}
          </p>
        )}
        {data?.days?.map((d) => (
          <Card key={d.date} data-testid="history-day-card" className="shadow-sm">
            <CardContent className="space-y-2 pt-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">{fmtKLDate(d.date)}</span>
                <span className="flex items-center gap-2">
                  {d.shiftName && (
                    <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-xs">{d.shiftName}</span>
                  )}
                  {d.hours != null && (
                    <span className="font-mono text-sm font-semibold tabular-nums">
                      {d.hours} {t("hours").toLowerCase()}
                    </span>
                  )}
                </span>
              </div>
              {(d.minutesLate != null || d.minutesEarly != null) && (
                <div className="flex flex-wrap gap-1.5">
                  {d.minutesLate != null && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900" data-testid="history-late-badge">
                      +{d.minutesLate} {t("minLate")}
                    </span>
                  )}
                  {d.minutesEarly != null && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900" data-testid="history-early-badge">
                      −{d.minutesEarly} {t("minEarly")}
                    </span>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg bg-secondary p-2">
                  <p className="text-xs text-muted-foreground">{t("inTime")}</p>
                  <p className="font-mono tabular-nums">{d.clockIn ? fmtKLTime(d.clockIn) : "—"}</p>
                  {d.clockInStatus && <StatusBadge kind="review" value={d.clockInStatus} className="mt-1" />}
                </div>
                <div className="rounded-lg bg-secondary p-2">
                  <p className="text-xs text-muted-foreground">{t("outTime")}</p>
                  <p className="font-mono tabular-nums">{d.clockOut ? fmtKLTime(d.clockOut) : "—"}</p>
                  {d.clockOutStatus && <StatusBadge kind="review" value={d.clockOutStatus} className="mt-1" />}
                </div>
              </div>
              {d.summary && (
                <p className="rounded-lg bg-secondary p-2 text-xs text-muted-foreground">{d.summary}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </AppShell>
  );
}
